import { useClientStore } from '@/stores/ClientStore'
import { useMoney } from '@/composables/app/useMoney'
import { useGoogleTagManager, useMixpanel, useSentry } from '@/vendors/integrations'
import { centsToAmount } from '@/utils/utils'
import { get as _get } from 'lodash'

export function useTrackingProcedure () {
  const gtm = useGoogleTagManager()
  const mixpanel = useMixpanel()
  const { withErrorHandler } = useSentry()

  const trackStepViewed = ({ step, procedure, procedure_execution, order, currency }) => {
    withErrorHandler(() => {
      mixpanel.track('Visited procedure step ' + step.index, {
        ...procedure.to_mixpanel,
        'Index': step.index,
        'Name': step.name
      })

      gtm.track({
        event: 'view_step',
        ...procedure.to_gtm,
        ecommerce: {
          ...procedure_execution.transaction?.to_gtm,
          items: getProducts({ items: order, procedure, currency })
        },
        step_index: step.index,
        step_name: step.name
      })

      gtm.reset()
    })
  }

  const trackStepNextClicked = () => withErrorHandler(() => mixpanel.track('Clicked next'))

  const trackProductItemListViewed = ({ products = [], procedure, currency }) => {
    withErrorHandler(() => {
      gtm.track({
        event: 'view_item_list',
        ...procedure.to_gtm,
        ecommerce: {
          items: getProducts({ items: products, procedure, currency })
        }
      })

      gtm.reset()
    })
  }

  const trackCartProductAdded = ({ product, quantity, index, procedure, currency }) => {
    withErrorHandler(() => {
      gtm.track({
        event: 'add_to_cart',
        ...procedure.to_gtm,
        ecommerce: {
          items: [getProduct({ product, quantity, index, procedure, currency })]
        }
      })

      gtm.reset()
    })
  }

  const trackCartProductRemoved = ({ product, quantity, index, procedure, currency }) => {
    withErrorHandler(() => {
      gtm.track({
        event: 'remove_from_cart',
        ...procedure.to_gtm,
        ecommerce: {
          items: [getProduct({ product, quantity, index, procedure, currency })]
        }
      })

      gtm.reset()
    })
  }

  const trackPaymentInfoAdded = ({ procedure_execution, procedure, order, currency }) => {
    withErrorHandler(() => {
      gtm.track({
        event: 'add_payment_info',
        ...procedure.to_gtm,
        ecommerce: {
          ...procedure_execution.transaction?.to_gtm,
          items: getProducts({ items: order, procedure, currency })
        }
      })

      gtm.reset()
    })
  }

  const trackCheckoutStarted = ({ procedure_execution, procedure, order }) => {
    withErrorHandler(() => {
      const transaction = procedure_execution.transaction
      const products = getProducts({ items: order, procedure, currency: transaction?.currency })

      mixpanel.track('Checkout', {
        ...procedure.to_mixpanel,
        'Cart': products
      })

      gtm.track({
        event: 'checkout',
        ...procedure.to_gtm,
        ecommerce: {
          ...transaction?.to_gtm,
          items: products
        }
      })

      gtm.reset()
    })
  }

  const trackExecutionCompleted = ({ procedure_execution, procedure, order }) => {
    withErrorHandler(() => {
      const transaction = procedure_execution.transaction
      const currency = transaction?.currency
      const fee = getFee({ procedure, procedure_execution, currency })
      const products = getProducts({ items: order, procedure, currency })

      mixpanel.track('Execution', {
        ...procedure.to_mixpanel,
        ...procedure_execution.to_mixpanel,
        'Cart': products
      })

      gtm.track({
        event: 'execution',
        ...procedure.to_gtm,
        ...procedure_execution.to_gtm,
        ecommerce: {
          ...transaction?.to_gtm,
          fee: fee,
          items: products
        }
      })

      gtm.reset()
    })
  }

  const trackTransactionCompleted = ({ procedure, procedure_execution, order }) => {
    withErrorHandler(() => {
      const transaction = procedure_execution.transaction
      const currency = transaction?.currency
      const fee = getFee({ procedure, procedure_execution, currency })
      const products = getProducts({ items: order, procedure, currency })

      mixpanel.track('New transaction', {
        ...procedure.to_mixpanel,
        ...procedure_execution.to_mixpanel,
        ...transaction?.to_mixpanel,
        'Cart': products
      })

      gtm.track({
        event: 'transaction',
        ...procedure.to_gtm,
        ...procedure_execution.to_gtm,
        ecommerce: {
          ...transaction?.to_gtm,
          fee: fee,
          items: products
        }
      })

      gtm.reset()
    })
  }

  const getProducts = ({ items = [], procedure, currency = null }) => items
    .map((product, index) => getProduct({ product, currency, procedure, index }))
    .filter(product => product)

  const getProduct = ({ product = {}, type = 'Product', currency, procedure, index = 0 }) => {
    const { getClient } = useClientStore()
    const { getAmount } = useMoney()

    const { client } = getClient()

    if (!product?.id) return

    return {
      index,
      currency,
      affiliation: 'Koalect Platform',
      item_id: product.id,
      item_name: product.full_name,
      item_brand: client.value?.name,
      item_category: Object.values(product.label || {}).find(label => label),
      item_category2: type,
      item_category3: procedure?.target_type,
      item_list_id: procedure?.id,
      item_list_name: Object.values(procedure?.name || {}).find(name => name),
      price: product.is_free ? 0 : centsToAmount(getAmount({ amount: product.price, currency })),
      quantity: product.quantity
    }
  }

  const getFee = ({ procedure, procedure_execution, currency }) => {
    const { getAmount } = useMoney()

    return _get(procedure_execution, 'actions_data.fee.active', false) ? centsToAmount(getAmount({ amount:_get(procedure, 'actions.fee.parameters.amount'), currency })) : 0
  }

  return {
    trackStepViewed,
    trackStepNextClicked,
    trackProductItemListViewed,
    trackCartProductAdded,
    trackCartProductRemoved,
    trackPaymentInfoAdded,
    trackCheckoutStarted,
    trackExecutionCompleted,
    trackTransactionCompleted
  }
}
